import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Play, 
  Pause, 
  Mail, 
  Send, 
  AlertTriangle, 
  Ban,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  CheckSquare,
  Square,
  Trash2,
  Eye,
  MousePointer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [logs, setLogs] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [bounceAnalysis, setBounceAnalysis] = useState(null);
  const [analyzingBounces, setAnalyzingBounces] = useState(false);

  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    try {
      // Recalculate stats from actual recipient statuses first
      await axios.post(`${API_URL}/campaigns/${id}/recalculate`).catch(() => {});
      const [campaignRes, logsRes, recipientsRes] = await Promise.all([
        axios.get(`${API_URL}/campaigns/${id}`),
        axios.get(`${API_URL}/campaigns/${id}/logs`),
        axios.get(`${API_URL}/campaigns/${id}/recipients`)
      ]);
      setCampaign(campaignRes.data);
      setLogs(logsRes.data);
      setRecipients(recipientsRes.data);
    } catch (error) {
      toast.error('Failed to fetch campaign');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromSendGrid = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/sendgrid/sync`);
      const s = response.data.synced;
      toast.success(`Synced: ${s.bounces} bounces, ${s.blocks} blocks, ${s.invalid} invalid. ${s.total_opens_7d || 0} opens this week.`);
      fetchCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const fetchBounceAnalysis = async () => {
    setAnalyzingBounces(true);
    try {
      const response = await axios.get(`${API_URL}/campaigns/${id}/bounce-analysis`);
      setBounceAnalysis(response.data);
    } catch (error) {
      toast.error('Failed to analyze bounces');
    } finally {
      setAnalyzingBounces(false);
    }
  };

  const handleSend = async () => {
    const selectedCount = recipients.filter(r => r.selected !== false).length;
    if (selectedCount === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API_URL}/campaigns/${id}/send`);
      toast.success(`Campaign sending started (${selectedCount} recipients)`);
      fetchCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sending');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this campaign? This will also delete all recipients and logs.')) return;
    try {
      await axios.delete(`${API_URL}/campaigns/${id}`);
      toast.success('Campaign deleted');
      navigate('/campaigns');
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const toggleRecipient = async (recipientId, currentSelected) => {
    const newSelected = !currentSelected;
    setRecipients(prev => prev.map(r => 
      r.id === recipientId ? { ...r, selected: newSelected } : r
    ));
    
    try {
      await axios.put(`${API_URL}/campaigns/${id}/recipients/${recipientId}`, { selected: newSelected });
    } catch (error) {
      // Revert on error
      setRecipients(prev => prev.map(r => 
        r.id === recipientId ? { ...r, selected: currentSelected } : r
      ));
      toast.error('Failed to update recipient');
    }
  };

  const selectAll = async (selected) => {
    const previousRecipients = [...recipients];
    setRecipients(prev => prev.map(r => ({ ...r, selected })));
    
    try {
      await axios.put(`${API_URL}/campaigns/${id}/select-all`, { selected });
      toast.success(selected ? 'All recipients selected' : 'All recipients deselected');
    } catch (error) {
      setRecipients(previousRecipients);
      toast.error('Failed to update recipients');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-muted text-muted-foreground',
      sending: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      paused: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      error: 'bg-destructive/10 text-destructive'
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  const getRecipientStatusIcon = (status) => {
    switch (status) {
      case 'opened': return <Eye className="w-4 h-4 text-blue-500" />;
      case 'clicked': return <MousePointer className="w-4 h-4 text-indigo-500" />;
      case 'delivered': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'sent': return <Send className="w-4 h-4 text-emerald-400" />;
      case 'failed': return <XCircle className="w-4 h-4 text-destructive" />;
      case 'bounced': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'blocked': return <Ban className="w-4 h-4 text-amber-500" />;
      case 'invalid': return <XCircle className="w-4 h-4 text-amber-500" />;
      case 'complained': return <Ban className="w-4 h-4 text-destructive" />;
      default: return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const exportLogs = () => {
    const csv = 'Email,Event,Timestamp\n' + 
      logs.map(l => `${l.email},${l.event},${l.timestamp}`).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign_${id}_logs.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!campaign) return null;

  const totalAttempted = (campaign.sent_count || 0) + (campaign.bounced_count || 0) + (campaign.failed_count || 0);
  const deliveryRate = totalAttempted > 0
    ? Math.round((campaign.sent_count / totalAttempted) * 100)
    : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaign-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{campaign.name}</h1>
              {getStatusBadge(campaign.status)}
            </div>
            <p className="text-muted-foreground text-sm">
              {campaign.domain} · Created {new Date(campaign.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSyncFromSendGrid} disabled={syncing} data-testid="sync-sendgrid-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from SendGrid'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchCampaign}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleDelete} className="text-destructive hover:text-destructive" data-testid="delete-campaign-btn">
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </Button>
          {campaign.status === 'draft' && (
            <Button onClick={handleSend} disabled={sending} data-testid="start-sending-btn">
              <Play className="w-4 h-4 mr-2" />
              {sending ? 'Starting...' : 'Start Sending'}
            </Button>
          )}
          {campaign.status === 'paused' && (
            <Button onClick={handleSend} disabled={sending}>
              <Play className="w-4 h-4 mr-2" />
              Resume
            </Button>
          )}
          {campaign.status === 'sending' && (
            <Button onClick={handleSend} disabled={sending} variant="outline">
              <Play className="w-4 h-4 mr-2" />
              {sending ? 'Resuming...' : 'Resume Sending'}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-primary" />
              <div>
                <p className="text-xl font-bold">{campaign.total_recipients}</p>
                <p className="text-xs text-muted-foreground">Recipients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Send className="w-6 h-6 text-emerald-500" />
              <div>
                <p className="text-xl font-bold">{campaign.sent_count || 0}</p>
                <p className="text-xs text-muted-foreground">Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Eye className="w-6 h-6 text-blue-500" />
              <div>
                <p className="text-xl font-bold">{campaign.opened_count || 0}</p>
                <p className="text-xs text-muted-foreground">Opened</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <div>
                <p className="text-xl font-bold">{campaign.bounced_count || 0}</p>
                <p className="text-xs text-muted-foreground">Bounced</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <XCircle className="w-6 h-6 text-destructive" />
              <div>
                <p className="text-xl font-bold">{campaign.failed_count || 0}</p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <div>
                <p className="text-xl font-bold">{deliveryRate}%</p>
                <p className="text-xs text-muted-foreground">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bounce Analysis — shows when there are bounces */}
      {(campaign.bounced_count || 0) > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4 pb-4">
            {!bounceAnalysis ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="font-medium text-sm">{campaign.bounced_count} emails bounced</p>
                    <p className="text-xs text-muted-foreground">Analyze bounce reasons to understand why</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={fetchBounceAnalysis} disabled={analyzingBounces} data-testid="analyze-bounces-btn">
                  {analyzingBounces ? 'Analyzing...' : 'Analyze Bounces'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Bounce Analysis — {bounceAnalysis.total_bounced} bounced emails
                  </p>
                  <Button size="sm" variant="ghost" onClick={() => setBounceAnalysis(null)} className="text-xs">Close</Button>
                </div>

                {/* Reason breakdown */}
                <div className="grid gap-3 md:grid-cols-2">
                  {Object.entries(bounceAnalysis.reason_breakdown).map(([category, data]) => (
                    <div key={category} className="rounded-lg border bg-background p-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">{category}</span>
                        <Badge variant="secondary" className="text-xs">{data.count}</Badge>
                      </div>
                      {data.sample_errors.length > 0 && (
                        <p className="text-xs text-muted-foreground break-all line-clamp-2" title={data.sample_errors[0]}>
                          {data.sample_errors[0]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Top bounced domains */}
                {bounceAnalysis.top_bounced_domains.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Top bounced domains:</p>
                    <div className="flex flex-wrap gap-2">
                      {bounceAnalysis.top_bounced_domains.map(d => (
                        <Badge key={d.domain} variant="outline" className="text-xs">
                          @{d.domain}: {d.count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="recipients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="recipients">
            Recipients ({recipients.filter(r => r.selected !== false).length}/{recipients.length} selected)
          </TabsTrigger>
          <TabsTrigger value="logs">Activity Log ({logs.length})</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        {/* Recipients Tab */}
        <TabsContent value="recipients">
          <Card>
            {campaign.status === 'draft' && (
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-lg">Select Recipients</CardTitle>
                  <CardDescription>Choose which recipients to include in this campaign</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => selectAll(true)}>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => selectAll(false)}>
                    <Square className="w-4 h-4 mr-1" />
                    Deselect All
                  </Button>
                </div>
              </CardHeader>
            )}
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {campaign.status === 'draft' && <TableHead className="w-12">Send</TableHead>}
                    <TableHead>Email</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipients.slice(0, 100).map((recipient) => (
                    <TableRow key={recipient.id} className={recipient.selected === false ? 'opacity-50' : ''}>
                      {campaign.status === 'draft' && (
                        <TableCell>
                          <Checkbox
                            checked={recipient.selected !== false}
                            onCheckedChange={() => toggleRecipient(recipient.id, recipient.selected !== false)}
                            data-testid={`select-recipient-${recipient.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium">{recipient.email}</TableCell>
                      <TableCell className="max-w-[200px]">
                        {recipient.data && (
                          <div className="text-xs text-muted-foreground truncate">
                            {Object.entries(recipient.data).slice(0, 2).map(([k, v]) => (
                              <span key={k} className="mr-2">{k}: {v}</span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getRecipientStatusIcon(recipient.status)}
                          <span className="capitalize">{recipient.status}</span>
                        </div>
                        {recipient.opened_at && !['bounced', 'blocked', 'invalid', 'failed', 'complained'].includes(recipient.status) && (
                          <p className="text-xs text-blue-500 mt-1">
                            Opened {new Date(recipient.opened_at).toLocaleString()}
                            {recipient.open_count > 1 && ` (${recipient.open_count}x)`}
                          </p>
                        )}
                        {recipient.error && (
                          <p className="text-xs text-destructive mt-1 truncate max-w-[200px]" title={recipient.error}>
                            {recipient.error}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {recipient.sent_at 
                          ? new Date(recipient.sent_at).toLocaleString() 
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {recipients.length > 100 && (
                <div className="p-4 text-center text-muted-foreground">
                  Showing 100 of {recipients.length} recipients
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Activity Log</CardTitle>
              <Button variant="outline" size="sm" onClick={exportLogs} disabled={logs.length === 0}>
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {logs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Badge className={
                            log.event === 'sent' ? 'bg-emerald-500/10 text-emerald-600' :
                            log.event === 'failed' ? 'bg-destructive/10 text-destructive' :
                            log.event === 'bounce' ? 'bg-amber-500/10 text-amber-600' :
                            log.event === 'complaint' ? 'bg-destructive/10 text-destructive' :
                            'bg-muted text-muted-foreground'
                          }>
                            {log.event}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{log.email}</TableCell>
                        <TableCell className="max-w-[300px] text-xs text-muted-foreground truncate">
                          {log.error || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  No activity yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Domain</p>
                  <p className="font-medium">{campaign.domain}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Template</p>
                  <p className="font-medium">{campaign.template_name}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Missing Variable Behavior</p>
                  <p className="font-medium capitalize">{campaign.missing_variable_behavior}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Warmup Mode</p>
                  <p className="font-medium">{campaign.warmup_mode ? 'Enabled' : 'Disabled'}</p>
                </div>
                {campaign.started_at && (
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Started At</p>
                    <p className="font-medium">{new Date(campaign.started_at).toLocaleString()}</p>
                  </div>
                )}
                {campaign.completed_at && (
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Completed At</p>
                    <p className="font-medium">{new Date(campaign.completed_at).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {campaign.column_mapping && (
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-2">Column Mapping</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(campaign.column_mapping).map(([variable, column]) => (
                      <Badge key={variable} variant="outline">
                        {`{${variable}}`} → {column}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignDetail;
